import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AcaIcon } from "../../shared/aca";
import { ROUTE_NAMES } from "../../../routes/routeConstants";
import { useAuth } from "../../../provider/AuthProvider";
import { useLoginModal } from "../../../context/LoginModalContext";
import { Job } from "../../../models/Job";
import { SUBJECT_CATEGORIES, SUBJECTS } from "../../../data/academicTaxonomy";
import { RegisterData, shortDate } from "../../sections/desk/useRegister";
import { Leaf, P } from "./shell";
import { VELLUM as T, MONO, display, mono, body, btn, input, DimLine, SheetHead, TitleBlock } from "./tokens";

/* A-01 the sheet, and A-02 the schedule of posts.

   One clause of the page this replaces, drawn as the folio's first two
   leaves: the sheet states what the register is, the schedule lists what it
   currently holds. Every string is the one the live page already carried. */

const OPENING = 6;

/* An entry stays a real link for a signed-in reader, so it can be opened in
   a new tab; a signed-out one is prompted to sign in, exactly as before. */
const Entry: React.FC<{ job: Job; n: number }> = ({ job, n }) => {
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const closes = shortDate(job.applicationDeadline);

  const style: React.CSSProperties = {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "66px minmax(0,1fr)",
    gap: 20,
    alignItems: "baseline",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderTop: `1px solid ${T.rule}`,
    borderRadius: 0,
    padding: "20px 0",
    textDecoration: "none",
    cursor: "pointer",
  };

  const inner = (
    <>
      <span style={{ ...mono(T, T.accent), display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span aria-hidden style={{ width: 9, height: 1, background: T.accent }} />
        {String(n).padStart(2, "0")}
      </span>
      <span>
        <span style={{ ...display(T, "clamp(18px, 2.2vw, 25px)"), display: "block" }} className={`${P}-entry-title`}>
          {job.title}
        </span>
        <span style={{ ...body(T), fontSize: 14.5, display: "block", marginTop: 7 }}>
          {(job.company || "").trim()}
          {job.location ? ` · ${job.location.trim()}` : ""}
        </span>
        <span style={{ ...mono(T), display: "block", marginTop: 9 }}>
          {[job.employmentType, job.salary, closes ? `closes ${closes}` : null].filter(Boolean).join("  ·  ")}
        </span>
      </span>
    </>
  );

  if (user) {
    return (
      <Link to={`${ROUTE_NAMES.PAGES.JOB_DETAILS}/${job.id}`} style={style} className={`${P}-entry`}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={openLoginModal} style={style} className={`${P}-entry`}>
      {inner}
    </button>
  );
};

/* ─── A-01 · sheet ───────────────────────────────────────────────── */

export const LeafSheet: React.FC<{ register: RegisterData }> = ({ register }) => {
  const { total, institutions, fieldsCovered, loading } = register;
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const edition = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      openLoginModal();
      return;
    }
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    const qs = params.toString();
    navigate(qs ? `${ROUTE_NAMES.PAGES.JOBS}?${qs}` : ROUTE_NAMES.PAGES.JOBS);
  };

  return (
    <Leaf
      first
      code="§ A-01 · sheet"
      measure={
        <>
          <div
            style={{
              ...mono(T),
              borderBottom: `1px solid ${T.rule}`,
              paddingBottom: 12,
              marginBottom: 34,
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <span>Acanetic · Register of academic posts</span>
            <span>{edition}</span>
          </div>

          <span className={`${P}-dim`}>
            <DimLine t={T} label={`${SUBJECT_CATEGORIES.length} fields`} />
          </span>

          <h1 style={{ ...display(T, "clamp(40px, 6.4vw, 74px)"), margin: "24px 0", lineHeight: 0.96, letterSpacing: "-0.042em" }}>
            The register of{" "}
            <span style={{ color: T.accent, borderBottom: `3px solid ${T.accent}` }}>academic posts</span>.
          </h1>

          <span className={`${P}-dim ${P}-dim-b`}>
            <DimLine t={T} label={`${SUBJECTS.length} subjects`} />
          </span>

          <p style={{ ...body(T), fontSize: 18, marginTop: 32, maxWidth: 600 }}>
            Every listing carries what academic hiring actually turns on - subfield, study level,
            academic role, contract. Read it in the terms you think in.
          </p>

          <form onSubmit={submit} style={{ marginTop: 30, maxWidth: 600 }}>
            <label htmlFor="landing-search" style={{ ...mono(T), display: "block", marginBottom: 8 }}>
              Search the register
            </label>
            <div className={`${P}-searchfield`} style={{ display: "flex", border: `1.5px solid ${T.ink}`, background: T.panel }}>
              <input
                id="landing-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. computational neuroscience"
                className={`${P}-input`}
                style={input(T)}
              />
              <button
                type="submit"
                className={`${P}-search-btn`}
                aria-label="Search the register"
                style={{ ...btn(T, "accent"), padding: "14px 22px", flexShrink: 0 }}
              >
                <AcaIcon name="search" size={13} stroke={T.onAccent} />
                <span>Search</span>
              </button>
            </div>
          </form>

          <p style={{ ...body(T), fontSize: 14, marginTop: 18, maxWidth: 520 }}>
            Kept in the open. Nothing is boosted, tiered or reordered by what an institution paid.
          </p>
        </>
      }
      margin={
        <TitleBlock
          t={T}
          rows={[
            ["Sheet", "A-01"],
            ["Posts open", loading ? "·" : String(total)],
            ["Institutions", loading ? "·" : String(institutions)],
            /* Reads "of 20" exactly as before - but derived, so it cannot
               drift from the schedule of fields further down the sheet. */
            ["Fields covered", loading ? "·" : `${fieldsCovered} of ${SUBJECT_CATEGORIES.length}`],
            ["Date", edition],
          ]}
        />
      }
    />
  );
};

/* ─── A-02 · schedule of posts ───────────────────────────────────── */

export const LeafPosts: React.FC<{ register: RegisterData }> = ({ register }) => {
  const { jobs, loading, error, total } = register;
  const { openRegisterModal } = useLoginModal();
  const opening = jobs.slice(0, OPENING);

  return (
    <Leaf
      code="§ A-02 · schedule of posts"
      measure={
        <>
          <SheetHead t={T} code={loading ? "·" : total === 0 ? "no entries" : `${opening.length} of ${total}`}>
            This edition
          </SheetHead>

          {loading && (
            <div aria-live="polite">
              <span className={`${P}-sr`}>Loading the register.</span>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  aria-hidden
                  className={`${P}-skeleton`}
                  style={{
                    padding: "20px 0",
                    borderTop: `1px solid ${T.rule}`,
                    animation: `${P}-pulse 1.6s infinite`,
                    animationDelay: `${i * 0.12}s`,
                  }}
                >
                  <div style={{ height: 16, width: "72%", background: T.rule }} />
                  <div style={{ height: 11, width: "44%", background: T.rule, marginTop: 10 }} />
                  <div style={{ height: 9, width: "56%", background: T.rule, marginTop: 12 }} />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: "28px 0", borderTop: `1px solid ${T.rule}` }}>
              <p style={display(T, "clamp(20px, 2.6vw, 26px)")}>{error}</p>
              <p style={{ ...body(T), marginTop: 10 }}>
                The entries below the fold are unaffected. Try again in a moment.
              </p>
            </div>
          )}

          {!loading && !error && opening.length === 0 && (
            <div style={{ padding: "30px 0", borderTop: `1px solid ${T.rule}`, maxWidth: 600 }}>
              <p style={display(T, "clamp(24px, 3.2vw, 34px)")}>No posts are listed yet.</p>
              <p style={{ ...body(T), marginTop: 14 }}>
                The register is being built. Take an account now and you will be reading the first
                entries as they are posted.
              </p>
              <button type="button" onClick={openRegisterModal} className={`${P}-accent`} style={{ ...btn(T, "accent"), marginTop: 24 }}>
                Take an account
              </button>
            </div>
          )}

          {!loading && !error && opening.map((j, i) => <Entry key={j.id} job={j} n={i + 1} />)}

          {!loading && !error && opening.length > 0 && (
            <Link
              to={ROUTE_NAMES.PAGES.JOBS}
              className={`${P}-rulelink`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                marginTop: 28,
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: T.ink,
                textDecoration: "none",
                borderBottom: `1.5px solid ${T.ink}`,
                paddingBottom: 4,
              }}
            >
              Read the full register
              <span style={{ fontSize: 13 }}>→</span>
            </Link>
          )}
        </>
      }
      margin={
        <div style={{ borderTop: `1.5px solid ${T.ink}`, paddingTop: 14 }}>
          <div style={{ ...mono(T, T.ink), marginBottom: 8 }}>Institutions</div>
          <p style={{ ...body(T), fontSize: 13.5, marginBottom: 14 }}>
            Hiring? Your post is read in the same measure, by the people who search these fields.
          </p>
          <Link
            to={ROUTE_NAMES.PAGES.POST_JOB}
            className={`${P}-rulelink`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontFamily: MONO,
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: T.accent,
              textDecoration: "none",
              borderBottom: `1px solid ${T.accent}`,
              paddingBottom: 3,
            }}
          >
            Post a position
            <span style={{ fontSize: 12 }}>→</span>
          </Link>
        </div>
      }
    />
  );
};
